/**
 * Packout MAS / Packout NC pack-size row — dual selector.
 *
 * Layout (matches the FDPUpdated mock's PackRow):
 *
 *   ┌── left: SAVED chips ──────────────────┐  ┌── right: CALC dropdown ──┐
 *   │ [✓] 100  [ ] 250  [✓] 500  + Add pack │  │ Calc: [Pack 100, Pack 500] │
 *   └────────────────────────────────────────┘  └─────────────────────────┘
 *
 * - LEFT chips toggle the *saved* flag (`selected`). Includes a free-text
 *   "+ Add pack" affordance for packs that aren't in AnnualMarketSales —
 *   a Packout-only feature so operators can record packs they actually
 *   packed out without those packs participating in the math.
 * - RIGHT MultiSelect drives the *calc* set (`used_in_calc`). Only packs
 *   that are saved (`selected=true`) appear in the dropdown options;
 *   selecting one here forces `selected=true` (calc ⇒ selected) and
 *   triggers the Annual / EU / SU recompute.
 *
 * Plain MAS / NC use the simpler `<PackSizeChips>` instead — the two
 * flags are always mirrored there so a single chip is sufficient.
 */

import { useState } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';

import { useEditStore } from './editStoreContext';
import styles from './packoutPackRow.module.scss';

const PackoutPackRow = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) return null;

  const data = store.workingByNDC.get(slice.ndc) ?? slice.serverData;

  // The two controls are INDEPENDENT and visually overlap freely:
  //   left chips -> reflect `selected`      (record sent to backend)
  //   right Calc -> reflects `used_in_calc` (drives Annual / EU / SU)
  //
  // Toggling a left chip ONLY edits `selected`; it never touches
  // `used_in_calc`, never recomputes, never affects the Calc dropdown.
  // Toggling Calc never touches the left chip's checked state.
  //
  // Two product rules govern the universes shown by each control:
  //   - User-added (free-text) packs live ONLY on the left. They can
  //     never enter the Calc dropdown — Calc is restricted to market
  //     packs because that's the only set we have AnnualMarketSales
  //     data for, and Calc drives the math.
  //   - Calc options = market packs only.
  //
  // The backend invariant `used_in_calc ⇒ selected` is enforced at
  // save-payload-construction time (see `toSavePayload` in editStore),
  // so the operator can freely uncheck a left chip even while that
  // pack is in Calc — on save, used_in_calc gets coerced to false.

  const marketPackSizes = Array.from(
    new Set(store.marketData.map(row => row.packsize))
  );
  const userAddedPackSizes = data.selected_packsizes
    .map(p => p.packsize)
    .filter(ps => !marketPackSizes.includes(ps));
  const allPacksForLeft = [...marketPackSizes, ...userAddedPackSizes];

  const calcSelected = data.selected_packsizes
    .filter(p => p.used_in_calc)
    .map(p => p.packsize);

  const isSelected = (ps: string): boolean =>
    data.selected_packsizes.find(p => p.packsize === ps)?.selected ?? false;

  return (
    <div className={styles.row}>
      <div className={styles.left}>
        <span className={styles.label}>Pack sizes:</span>
        {allPacksForLeft.map(ps => {
          const inputId = `pack-saved-${slice.ndc}-${ps}`;
          const checked = isSelected(ps);
          return (
            <label key={ps} htmlFor={inputId} className={styles.chip}>
              <Checkbox
                inputId={inputId}
                checked={checked}
                onChange={() => store.setPackSelected(slice.ndc, ps, !checked)}
              />
              <span>{formatPackLabel(ps)}</span>
            </label>
          );
        })}
        <AddPackInline ndc={slice.ndc} />
      </div>
      <div className={styles.right}>
        <span className={styles.label}>Calc:</span>
        <MultiSelect
          value={calcSelected}
          options={marketPackSizes.map(ps => ({
            label: `Pack ${formatPackLabel(ps)}`,
            value: ps,
          }))}
          onChange={(e: MultiSelectChangeEvent) => {
            const next = (e.value as string[]) ?? [];
            // Refuse empty calc so the math never drops to all-zero by
            // accident. Operator must pick a different pack first.
            if (next.length === 0) return;
            store.setCalcPacks(slice.ndc, next);
          }}
          placeholder="Select packs"
          display="chip"
          showSelectAll={false}
          className={styles.calcMulti}
        />
      </div>
    </div>
  );
});

const AddPackInline = observer(({ ndc }: { ndc: string }) => {
  const store = useEditStore();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setAdding(true);
    setDraft('');
    setError(null);
  };
  const cancel = () => {
    setAdding(false);
    setDraft('');
    setError(null);
  };
  const confirm = () => {
    const trimmed = draft.trim();
    if (!/^[0-9]+(\.[0-9]+)?$/.test(trimmed) || parseFloat(trimmed) <= 0) {
      setError('Enter a positive number');
      return;
    }
    const ok = store.addUserPackSize(ndc, trimmed);
    if (!ok) {
      setError('Pack size already exists');
      return;
    }
    cancel();
  };

  if (!adding) {
    return (
      <Button
        type="button"
        label="+ Add pack"
        outlined
        onClick={start}
        className={styles.addButton}
      />
    );
  }

  return (
    <div className={styles.addInline}>
      <InputText
        autoFocus
        value={draft}
        placeholder="e.g. 30"
        onChange={e => {
          setDraft(e.target.value);
          setError(null);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') confirm();
          if (e.key === 'Escape') cancel();
        }}
        className={styles.addInput}
      />
      <Button label="Add" onClick={confirm} className={styles.addConfirm} />
      <Button label="Cancel" outlined onClick={cancel} className={styles.addCancel} />
      {error && <span className={styles.addError}>{error}</span>}
    </div>
  );
});

function formatPackLabel(packsize: string): string {
  const n = Number(packsize);
  if (!Number.isFinite(n)) return packsize;
  return Number.isInteger(n) ? String(Math.trunc(n)) : String(n);
}

export default PackoutPackRow;
